import React from 'react';
import { Avatar, Button, Card } from '@parsina/framework';
import { ChevronLeft } from 'lucide-react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import StatusChip from '@parsina/framework/clinical/src/components/common/StatusChip';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

// One search hit (§B.3). The whole card opens the record — there is exactly one
// thing to do with a found patient, and a button inside the card would be a
// smaller target than the card.
//
// The meta line is what a doctor uses to tell two «محمد رضایی»s apart: age,
// file number and the national id. Those are LTR-isolated runs
// (`.parsina-ltr`), or the digits and their punctuation land at the wrong end
// of an RTL line.

const Dot = () => (
  <span aria-hidden style={{ color: C.textMuted, opacity: 0.6 }}>·</span>
);

const MobilePatientResultCard = ({ patient, onOpen }) => {
  const { t, formatDate, formatDigits } = useTranslation();

  const meta = [];
  if (patient.age != null && patient.age !== '') {
    meta.push(<span key="age">{formatDigits(patient.age)} {t('common.units.year')}</span>);
  }
  if (patient.fileNumber) {
    meta.push(
      <span key="file" className="parsina-ltr" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatDigits(patient.fileNumber)}
      </span>,
    );
  }
  if (patient.nationalId) {
    meta.push(
      <span key="nid" className="parsina-ltr" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatDigits(patient.nationalId)}
      </span>,
    );
  }

  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <Button
        variant="ghost"
        data-testid={`lookup-result-${patient.id}`}
        aria-label={t('encounters.recordOf', { name: patient.name || t('encounters.unknownPatient') })}
        onClick={() => onOpen(patient)}
        className="h-auto min-h-11 w-full items-center gap-2.5 rounded-none p-3 text-start"
      >
        <Avatar name={patient.name || ''} size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            dir="auto"
            style={{
              fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {patient.name || t('encounters.unknownPatient')}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.3125rem', marginTop: '0.125rem',
            fontSize: '0.6875rem', color: C.textMuted,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {meta.map((node, i) => (
              <React.Fragment key={node.key}>{i > 0 && <Dot />}{node}</React.Fragment>
            ))}
          </div>
          {patient.lastEncounter && (
            <div style={{ fontSize: '0.625rem', color: C.textMuted, marginTop: '0.125rem' }}>
              {t('patient.lastVisit')} {formatDate(patient.lastEncounter, 'long')}
            </div>
          )}
        </div>
        {patient.status && <StatusChip status={patient.status} />}
        <ChevronLeft
          size={16}
          aria-hidden
          style={{ color: C.textMuted, flexShrink: 0, transform: 'scaleX(var(--dir-flip))' }}
        />
      </Button>
    </Card>
  );
};

export default MobilePatientResultCard;
